/**
 * Permission-based RBAC system
 * Pure TypeScript — no Convex imports so both backend and frontend can use it.
 */

export const ROLES = [
	"admin",
	"sales",
	"logistics",
	"accounting",
	"inventory",
	"warehouse",
] as const;

export type Role = (typeof ROLES)[number];

export type Permission =
	| "inventory:view"
	| "inventory:edit"
	| "logistics:view"
	| "logistics:edit"
	| "orders:view"
	| "orders:edit"
	| "sales:view"
	| "sales:edit"
	| "contracts:view"
	| "contracts:edit"
	| "contracts:approve"
	| "accounting:view"
	| "accounting:edit"
	| "accounting:approve"
	| "accounting:manage"
	| "payments:view"
	| "payments:edit"
	| "users:view"
	| "users:edit"
	| "admin:view"
	| "admin:manage"
	| "marketing:view"
	| "marketing:edit"
	| "documents:view"
	| "documents:generate"
	| "carriers:view"
	| "carriers:edit"
	| "samples:view"
	| "samples:edit"
	| "points:view"
	| "points:edit"
	| "internal:access";

const ROLE_PERMISSIONS: Record<Exclude<Role, "admin">, readonly Permission[]> = {
	sales: [
		"internal:access",
		"sales:view",
		"sales:edit",
		"contracts:view",
		"orders:view",
		"inventory:view",
		"logistics:view",
		"carriers:view",
		"documents:view",
		"samples:view",
		"samples:edit",
		"users:view",
	],
	logistics: [
		"internal:access",
		"logistics:view",
		"logistics:edit",
		"orders:view",
		"orders:edit",
		"inventory:view",
		"inventory:edit",
		"documents:view",
		"documents:generate",
		"carriers:view",
		"carriers:edit",
		"users:view",
		"payments:view",
		"payments:edit",
	],
	warehouse: [
		"internal:access",
		"logistics:view",
		"logistics:edit",
		"orders:view",
		"orders:edit",
		"inventory:view",
		"inventory:edit",
	],
	accounting: [
		"internal:access",
		"accounting:view",
		"accounting:edit",
		"accounting:approve",
		"accounting:manage",
		"payments:view",
		"payments:edit",
		"orders:view",
	],
	inventory: [
		"internal:access",
		"inventory:view",
		"inventory:edit",
		"orders:view",
		"sales:view",
		"sales:edit",
		"contracts:view",
		"users:view",
		"documents:view",
		"samples:view",
		"samples:edit",
		"carriers:view",
	],
};

export function normalizeRole(role: string | null | undefined): Role | null {
	if (!role) return null;
	const lower = role.toLowerCase();
	return ROLES.includes(lower as Role) ? (lower as Role) : null;
}

export function hasPermission(
	role: string | null | undefined,
	permission: Permission,
): boolean {
	const normalized = normalizeRole(role);
	if (!normalized) return false;
	if (normalized === "admin") return true;
	return (ROLE_PERMISSIONS[normalized] as readonly Permission[]).includes(permission);
}

const INTERNAL_EVENT_ACCESS_EMAILS = [
	"russell@anthemcoffeeimports.com",
	"colton@anthemcoffeeimports.com",
] as const;
const MARKETING_CAMPAIGN_OPERATOR_EMAILS = [
	"russell@anthemcoffeeimports.com",
	"colton@anthemcoffeeimports.com",
] as const;
const ACCOUNTING_BILLS_REVIEW_EMAILS = [
	"jv@anthemcoffeeimports.com",
] as const;
const PERMANENT_BILL_DELETE_EMAILS = [
	"jacob@anthemcoffeeimports.com",
	"yash@anthemcoffeeimports.com",
] as const;

function normalizeEmailForPermission(email: string | null | undefined): string | null {
	const normalized = email?.trim().toLowerCase();
	return normalized ? normalized : null;
}

export function isInternalEventAccessEmail(email: string | null | undefined): boolean {
	const normalized = normalizeEmailForPermission(email);
	return Boolean(
		normalized &&
			(INTERNAL_EVENT_ACCESS_EMAILS as readonly string[]).includes(normalized),
	);
}

export function canAccessInternalEvents(
	role: string | null | undefined,
	email: string | null | undefined,
): boolean {
	if (hasPermission(role, "admin:view")) return true;
	if (normalizeRole(role) === "sales") return true;
	return isInternalRole(role) && isInternalEventAccessEmail(email);
}

export function canManageInternalEvents(
	role: string | null | undefined,
	email: string | null | undefined,
): boolean {
	if (hasPermission(role, "admin:manage")) return true;
	return isInternalRole(role) && isInternalEventAccessEmail(email);
}

export function isMarketingCampaignOperatorEmail(email: string | null | undefined): boolean {
	const normalized = normalizeEmailForPermission(email);
	return Boolean(
		normalized &&
			(MARKETING_CAMPAIGN_OPERATOR_EMAILS as readonly string[]).includes(
				normalized,
			),
	);
}

export function canManageMarketingCampaigns(
	role: string | null | undefined,
	email: string | null | undefined,
): boolean {
	if (hasPermission(role, "admin:manage")) return true;
	return isInternalRole(role) && isMarketingCampaignOperatorEmail(email);
}

export function canAccessMarketingEmails(
	role: string | null | undefined,
	email: string | null | undefined,
): boolean {
	return hasPermission(role, "sales:view") || canManageMarketingCampaigns(role, email);
}

/**
 * Limited access to Bills & Expenses and its ACI cost rollup.
 *
 * This deliberately does not grant the broad accounting:view permission:
 * named reviewers keep their existing operational role and cannot browse the
 * rest of Accounting.
 */
export function canViewAccountingBills(
	role: string | null | undefined,
	email: string | null | undefined,
): boolean {
	if (hasPermission(role, "accounting:view")) return true;
	const normalized = normalizeEmailForPermission(email);
	return Boolean(
		isInternalRole(role) &&
			normalized &&
			(ACCOUNTING_BILLS_REVIEW_EMAILS as readonly string[]).includes(normalized),
	);
}

/**
 * JV may hand off or approve a bill only while it is explicitly assigned to
 * their logistics profile. Bill ownership is enforced by each mutation.
 */
export function canReviewAssignedAccountingBills(
	role: string | null | undefined,
	email: string | null | undefined,
): boolean {
	const normalized = normalizeEmailForPermission(email);
	return Boolean(
		normalizeRole(role) === "logistics" &&
			normalized &&
			(ACCOUNTING_BILLS_REVIEW_EMAILS as readonly string[]).includes(normalized),
	);
}

export function canPermanentlyDeleteAccountingBills(
	role: string | null | undefined,
	email: string | null | undefined,
): boolean {
	const normalized = normalizeEmailForPermission(email);
	return Boolean(
		hasPermission(role, "accounting:manage") &&
			normalized &&
			(PERMANENT_BILL_DELETE_EMAILS as readonly string[]).includes(normalized),
	);
}

export function hasAnyPermission(
	role: string | null | undefined,
	permissions: Permission[],
): boolean {
	return permissions.some((p) => hasPermission(role, p));
}

export function isInternalRole(role: string | null | undefined): boolean {
	return normalizeRole(role) !== null;
}

/** Internal roasting pilot: admins and Russell's named internal account. */
export function canAccessRoasting(
	role: string | null | undefined,
	email?: string | null,
): boolean {
	return (
		normalizeRole(role) === "admin" ||
		(isInternalRole(role) &&
			email?.trim().toLowerCase() === "russell@anthemcoffeeimports.com")
	);
}
